import { lazy, type ComponentType, type LazyExoticComponent } from "react";

type Preloadable<T extends ComponentType<any>> = LazyExoticComponent<T> & { preload: () => Promise<{ default: T }> };

function route<T extends ComponentType<any>>(importer: () => Promise<{ default: T }>): Preloadable<T> {
    let pending: Promise<{ default: T }> | null = null;
    const load = () => {
        if (!pending) {
            pending = importer().catch((error) => {
                pending = null;
                throw error;
            });
        }
        return pending;
    };
    const component = lazy(load) as Preloadable<T>;
    component.preload = load;
    return component;
}

export const LazyHomePage = route(() => import("features/groups/pages/home/HomePage"));
export const LazyGroupDetails = route(() => import("features/groups/pages/groups/GroupDetailsPage"));
export const LazyGroupEntrance = route(() => import("features/groups/pages/groups/GroupEntrance"));
export const LazyInviteLanding = route(() => import("features/groups/pages/groups/InviteLanding"));
export const LazyBudgetMenu = route(() => import("features/budget/pages/BudgetMenu"));
export const LazyPersonalCalendarPage = route(() => import("features/budget/pages/PersonalCalendarPage"));
export const LazyInvestmentsMenu = route(() => import("features/investments/pages/InvestmentsMenu"));
export const LazyCotizacionesMenu = route(() => import("features/market/pages/CotizacionesMenu"));
export const LazyWalletPage = route(() => import("features/wallet/pages/WalletPage"));
export const LazyError404 = route(() => import("pages/errors/Error404"));
export const LazyProfilePage = route(() => import("features/profile/pages/ProfilePage"));
export const LazySignIn = route(() => import("features/auth/components/auth/SignIn"));
export const LazyRegister = route(() => import("features/auth/components/auth/Register"));
export const LazyVerifyEmail = route(() => import("features/auth/components/auth/VerifyEmail"));
export const LazyResetPassword = route(() => import("features/auth/components/auth/ResetPassword"));
export const LazyAdsLandingPage = route(() => import("pages/landing/AdsLandingPage"));

export function preloadRouteForHref(pathname: string): void {
    const parts = pathname.split("/").filter(Boolean);
    const routeParts = parts[0] === "es" || parts[0] === "en" ? parts.slice(1) : parts;
    const [first, second, third, fourth] = routeParts;
    let preload: (() => Promise<unknown>) | undefined;

    if (!first || first === "login") preload = LazySignIn.preload;
    else if (first === "register") preload = LazyRegister.preload;
    else if (first === "verify-email") preload = LazyVerifyEmail.preload;
    else if (first === "reset-password") preload = LazyResetPassword.preload;
    else if (first === "landing") preload = LazyAdsLandingPage.preload;
    else if (first === "invite") preload = LazyInviteLanding.preload;
    else if (second === "groups" && fourth === "invite") preload = LazyGroupEntrance.preload;
    else if (second === "groups" && third) preload = LazyGroupDetails.preload;
    else if (second === "groups") preload = LazyHomePage.preload;
    else if (second === "personal" && third === "calendar") preload = LazyPersonalCalendarPage.preload;
    else if (second === "personal") preload = LazyBudgetMenu.preload;
    else if (second === "cotizaciones") preload = LazyCotizacionesMenu.preload;
    else if (second === "investments") preload = LazyInvestmentsMenu.preload;
    else if (second === "wallet" || second === "accounts") preload = LazyWalletPage.preload;
    else if (!second || second === "profile") preload = LazyProfilePage.preload;
    else if (fourth) preload = LazyGroupEntrance.preload;

    if (preload) void preload().catch(() => undefined);
}
